import { useRef, useState } from 'react';

import { usePushOnce } from '@/hooks/usePushOnce';
import { ERROR_COPY, OUTDATED_APP_COPY, userFacingError } from '@/lib/dealCtaErrors';
import { openWebUrl, reserveWebUrl } from '@/lib/openWebUrl';
import { stepIsOpen } from '@/lib/socialProgress';
import { API_ERROR_CODE, isApiError, messageFor } from '@/services/errors';
import { voltrService } from '@/services/voltr';
import { dealsStore } from '@/stores/deals.store';
import { voltrStore } from '@/stores/voltr.store';
import type { Action, Deal, DealCta, DealStep } from '@/types/deal';

const BRIEF_BLOCKED = "The playbook didn't open. If your browser blocked a pop-up, allow it and tap again.";
const LINK_BLOCKED = "The page didn't open. If your browser blocked a pop-up, allow it and tap again.";

/**
 * A step's advance button on the deal page: marks the step done, creating the
 * user's Action first when there isn't one, and turns failures into
 * athlete-facing copy.
 */
export function useDealCta(deal: Deal | undefined, action: Action | undefined) {
  const [ctaError, setCtaError] = useState<string | null>(null);
  // MarketPryce Social taps (Apply, Read the playbook) don't run through
  // dealsStore.clicking/advancing, so they carry their own busy flag: the
  // button spins and a second tap is dropped.
  const [socialBusy, setSocialBusy] = useState(false);
  const busyRef = useRef(false);
  const pushOnce = usePushOnce();

  async function whileBusy(work: () => Promise<void>) {
    if (busyRef.current) return;
    busyRef.current = true;
    setSocialBusy(true);
    try {
      await work();
    } finally {
      busyRef.current = false;
      setSocialBusy(false);
    }
  }

  async function handleCtaPress(step: DealStep, cta?: DealCta) {
    if (!deal) return;
    // MarketPryce Social (Voltr) steps run in the app, not through /advance.
    if (cta?.action === 'voltr_pick_product') {
      setCtaError(null);
      pushOnce(`/deals/order/${deal.slug}`);
      return;
    }
    if (cta?.action === 'voltr_start') {
      await whileBusy(async () => {
        setCtaError(null);
        // An answer landing after logout is the previous user's: dropped.
        const mark = dealsStore.actionMark();
        try {
          const started = await voltrService.start(deal.slug);
          // Approved members get the brand's products and brief from /social,
          // which answered "not a member" before this tap.
          if (dealsStore.applyActionSince(mark, started)) void voltrStore.load(deal.slug);
        } catch (err) {
          // The server authors Voltr error copy (e.g. "application wasn't approved").
          setCtaError(messageFor(err, userFacingError(err)));
        }
      });
      return;
    }
    // The upload step uploads in place (SocialInlineUpload); the deal page scrolls the hero's button to it.
    if (cta?.action === 'voltr_upload') {
      setCtaError(null);
      return;
    }
    if (cta?.action === 'voltr_brief') {
      await readBrief(step);
      return;
    }
    if (cta?.action === 'voltr_open_link') {
      await openAndAdvance(step, cta);
      return;
    }
    if (cta?.action === 'open_link') {
      setCtaError(null);
      if (cta.url) openWebUrl(cta.url);
      return;
    }
    // Only an advance marks the step done. An action this build doesn't know
    // (a newer server) must not complete the step behind the user's back.
    if (cta && cta.action !== 'advance') {
      setCtaError(OUTDATED_APP_COPY);
      return;
    }
    setCtaError(null);
    // Full deal: a brand-new signup has no funded spot this month, so don't
    // fire a click the server will 422 (deal_full). Users already mid-flow
    // (an Action exists) keep their spot and fall through to advance.
    if (deal.full && !action) {
      setCtaError(ERROR_COPY[API_ERROR_CODE.DealFull] ?? "Couldn't continue. Try again.");
      return;
    }
    // CTA always means advance — the user is manually marking this
    // step done. If no Action exists yet (server returns 404), call
    // /click first to silently create one, then retry. The brand
    // redirect URL from /click is intentionally ignored — the user is
    // saying "I already did the thing," not "open the link." Opening
    // the link is a separate concern (just URL text in the description
    // for now).
    try {
      if (!action) {
        // dealsStore.click already calls refreshAction internally — no
        // need for an extra round-trip here.
        await dealsStore.click(deal.slug);
      }
      const fresh = dealsStore.actionFor(deal.id);
      if (!fresh) {
        setCtaError("Couldn't continue. Try again.");
        return;
      }
      await dealsStore.advance(fresh.id, step.key);
    } catch (err) {
      // step_out_of_order means our local view of the active step is
      // stale. Server's response includes the correct active_step_key
      // in details; refreshing the action lets the stepper rebuild
      // against fresh state instead of showing the same wrong CTA.
      if (isApiError(err) && err.code === API_ERROR_CODE.StepOutOfOrder && deal) {
        void dealsStore.refreshAction(deal.id).catch(() => {});
      }
      setCtaError(userFacingError(err));
    }
  }

  /**
   * MarketPryce Social: open the deal's playbook, and when `step` (the
   * playbook step) is open, mark it done. The server opens that step as soon
   * as the product is ordered, so it can be read while the package ships. The
   * step is only marked done once the playbook actually opened, or the server
   * answered that there is none (nothing more to read). A failed load or a
   * blocked tab leaves the step open and says why.
   */
  async function readBrief(step?: DealStep) {
    // A tap while the last one is still working is dropped before it claims
    // a tab; otherwise the tab has to be claimed now, inside the tap, on web.
    if (!deal || !action || busyRef.current) return;
    const open = reserveWebUrl();
    await whileBusy(async () => {
      setCtaError(null);
      try {
        const url = await voltrService.briefUrl(action.id);
        if (url && !(await open(url))) {
          setCtaError(BRIEF_BLOCKED);
          return;
        }
        if (!url) await open(null);
        if (step && stepIsOpen(action, step)) await dealsStore.advance(action.id, step.key);
      } catch (err) {
        await open(null);
        setCtaError(messageFor(err, userFacingError(err)));
      }
    });
  }

  /**
   * A brand's own page (Project Repat's quilt order): open the step's link,
   * then mark the step done, like the playbook. Nothing to fetch first, but
   * the tab is still claimed inside the tap so web doesn't block it.
   */
  async function openAndAdvance(step: DealStep, cta: DealCta) {
    if (!deal || !action || busyRef.current) return;
    const open = reserveWebUrl();
    await whileBusy(async () => {
      setCtaError(null);
      try {
        if (!(await open(cta.url ?? null))) {
          setCtaError(LINK_BLOCKED);
          return;
        }
        if (stepIsOpen(action, step)) await dealsStore.advance(action.id, step.key);
      } catch (err) {
        setCtaError(messageFor(err, userFacingError(err)));
      }
    });
  }

  return { ctaError, handleCtaPress, readBrief, socialBusy };
}
